/* eslint-disable class-methods-use-this */
import { History } from 'history';
import { Dispatch } from 'redux';
import { TcsmEpiApiState } from '../../../../../../root/tcsm-model';
import { GpsType } from '../../../../../../global/components/layout/layout-models';
import { DBPlanAdoptionContentModel, DbPlanFormModel, DbPlanSubmitRequest, QccoStatus } from '../models/plan-adoption-form-models';

import { DbPlanAdoptionWizard } from './db-plan-adoption-wizard';
import { getSummaryPlan, postDbPlan, validateDbPlan } from '../../../../../../global/ducks/benadmin-api';
import { PensionPlanTypes } from '../../plan-history/plan-history-models';
import { dispatchSetPlanSummary } from '../../institution-slice';
import { DocuSignRequest, DocuSignResult } from '../../../docusign/models/docusign-models';
import { apiErrorClear } from '../../../../../../global/ducks/base-api';
import { getFormattedDate } from '../../../../../../global/helpers/date-helpers';
import { Institution } from '../../../../../../global/models/institution-model';
import { formatDate, formatDateMonthNumber } from '../../../../../../global/helpers/form-data-helper';
import { calculateQccoStatus, createQccoValidationSchema } from '../shared/qcco-questions-shared';
import { YesNoIndicators } from '../../../../../../global/models/api-model';

export class LayDbPlanAdoptionWizard extends DbPlanAdoptionWizard {

	readonly onSuccessUrl: string;

	private currentRequest: DbPlanSubmitRequest;

	constructor(dispatch: Dispatch<any>, history: History<unknown>, epiContent: TcsmEpiApiState, onSuccessUrl: string) {
		super(dispatch, history, epiContent);
		this.onSubmit = this.layDbSubmit;
		this.onSuccessUrl = onSuccessUrl;
	}


	private onValidateSuccess() {
		this.currentRequest.toggleDocusign(true);
	}

	onSubmitSuccess(clientNumber: string, envelopeId: string) {
		this.dispatch(getSummaryPlan(clientNumber, dispatchSetPlanSummary(this.dispatch)));
		this.dispatch(apiErrorClear());
		this.history?.push({
			pathname: this.onSuccessUrl,
			search: `?formType=${PensionPlanTypes.LayDb}`,
			state: {
				request: {
					values: { envelopeId },
				},
			},
		});
	}

	getCustomFields(institution: Institution, indenmnified?: boolean) {
		const initialValues = this.currentRequest?.values || this.getFormInitialValue();
		const labelSettings = this.epiContent?.labelSettingState?.epiContent;

		const getQccoQuestionValue = (questionValue: string) => {
			if (questionValue === YesNoIndicators.Yes) {
				return labelSettings?.yesText;
			}
			if (questionValue === YesNoIndicators.No) {
				return labelSettings?.noText;
			}
			return YesNoIndicators.NotApplicable;
		};

		const fields = [
			{
				tabLabel: 'client_id',
				value: institution.org_client_nbr,
			},
			{
				tabLabel: 'adoption_date',
				value: getFormattedDate(initialValues?.adoption_date),
			},
			{
				tabLabel: 'institution_name',
				value: institution.organization_name,
			},
			{
				tabLabel: 'address_1',
				value: `${institution.physical_address_1} ${institution.physical_address_2} ${institution.physical_address_3}`,
			},
			{
				tabLabel: 'address_2',
				value: `${institution.physical_city} ${institution.physical_state} ${institution.physical_country} ${institution.physical_postal_code}`,
			},
			{
				tabLabel: 'qcco_qualified',
				value: this.calculateQccoStatus(initialValues) === QccoStatus.Qualified ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'complyEpiscopalChurchLayPensionSystem',
				value: initialValues?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'supported_by_church',
				value: initialValues?.qccoQuestion1 === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'offers_services_to_public',
				value: getQccoQuestionValue(initialValues?.qccoQuestion2),
			},
			{
				tabLabel: 'receives_gov_support',
				value: getQccoQuestionValue(initialValues?.qccoQuestion3),
			},
		];

		if (indenmnified)
			fields.push({
				tabLabel: 'indemnified',
				value: 'Completed',
			});
		return fields;
	}

	getDocusignOption(docuSignRequest: DocuSignRequest, toggleDocusign: any, institution: Institution, indenmnified: boolean) {
		const customTextTabs = this.getCustomFields(institution, indenmnified);
		return {
			request: {
				...docuSignRequest,
				tabs: { textTabs: customTextTabs },
			},
			finishText: this.epiContent?.institutionSettingsState?.epiContent.layDbDocuSignFinishText,
			rejectText: this.epiContent?.institutionSettingsState?.epiContent.docuSignRejectText,
			onDecline: () => {
				toggleDocusign(false);
			},
			onSigningComplete: (result: DocuSignResult) => {
				const payload = this.createSubmitModel(this.currentRequest.values, result?.envelopeId);
				this.dispatch(postDbPlan(this.currentRequest?.clientId, payload, () => this.onSubmitSuccess(this.currentRequest?.clientId, result?.envelopeId)));
			},
		};
	}

	getIndemnifyDocusignOption(docuSignRequest: DocuSignRequest, toggleDocusign: any, onComplete: any, institution: Institution) {
		const institutionAddress = `${institution?.physical_address_1}${institution?.physical_address_2}${institution?.physical_address_3}, ${institution?.physical_city}, ${institution?.physical_state} ${institution?.physical_postal_code}`;
		const initialValues = this.currentRequest?.values || this.getFormInitialValue();
		const labelSettings = this.epiContent?.labelSettingState?.epiContent;

		const getQccoQuestionValue = (questionValue: string) => {
			if (questionValue === YesNoIndicators.Yes) {
				return labelSettings?.yesText;
			}
			if (questionValue === YesNoIndicators.No) {
				return labelSettings?.noText;
			}
			return YesNoIndicators.NotApplicable;
		};

		const customTextTabs = [
			{
				tabLabel: 'organization_name',
				value: institution.organization_name,
			},
			{
				tabLabel: 'organization_address',
				value: institutionAddress,
			},
			{
				tabLabel: 'qcco_qualified',
				value: this.calculateQccoStatus(initialValues) === QccoStatus.Qualified ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'complyEpiscopalChurchLayPensionSystem',
				value: initialValues?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'supported_by_church',
				value: initialValues?.qccoQuestion1 === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'offers_services_to_public',
				value: getQccoQuestionValue(initialValues?.qccoQuestion2),
			},
			{
				tabLabel: 'receives_gov_support',
				value: getQccoQuestionValue(initialValues?.qccoQuestion3),
			},
		];
		return {
			request: {
				...docuSignRequest,
				tabs: { textTabs: customTextTabs },
			},
			finishText: this.epiContent?.institutionSettingsState?.epiContent.indemnifyLayDbDocuSignFinishText,
			rejectText: this.epiContent?.institutionSettingsState?.epiContent.indemnifyLayDbDocuSignRejectText,
			onDecline: () => {
				toggleDocusign(false);
			},
			onSigningComplete: () => {
				onComplete();
			},
		};
	}

	getFormContentModel(): DBPlanAdoptionContentModel {
		return {
			pageTitle: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBTitle,
			PageDescription: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBDescription,
			planAdoptionDetailTitle: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBDetailsTitle,
			adoptingInstitutionLabel: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBAdoptionInstitutionTitle,
			clientIdLabel: this.epiContent?.institutionSettingsState?.epiContent?.clientIdLabel,
			adoptionDateLabel: this.epiContent?.institutionSettingsState?.epiContent?.planAdoptionDateLabel,
			signHereLabel: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBSignLabel,
			signHereTooltip: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBSignTooltip,
			signHereDescription: this.epiContent?.institutionSettingsState?.epiContent?.adoptLayDBSignHelper,
			gpsType: GpsType.LayDbAdoption,
			indemnifyWarningTitleDc: this.epiContent?.institutionSettingsState?.epiContent?.layDbIndemnifyWarningTitleDc,
			indemnifyWarningTitleRsvp: this.epiContent?.institutionSettingsState?.epiContent?.layDbIndemnifyWarningTitleRsvp,
			indemnifyWarningRsvpMessage: this.epiContent?.institutionSettingsState?.epiContent?.layDbIndemnifyRsvpWarningMessage,
			indemnifyWarningMessage: this.epiContent?.institutionSettingsState?.epiContent?.layDbIndemnifyWarningMessage,
			indemnifyButtonText: this.epiContent?.institutionSettingsState?.epiContent?.indemnifyButtonText,
			indemnifyButtonHelper: this.epiContent?.institutionSettingsState?.epiContent?.indemnifyButtonHelper,
			indemnifySuccessMessage: this.epiContent?.institutionSettingsState?.epiContent?.layDbIndemnifySuccessMessage,
			complyEpiscopalChurchLayPensionSystemQuestion: this.epiContent?.institutionSettingsState?.epiContent?.adoptRSVPComplyEpiscopalChurchLayPensionSystemQuestion,
			layDBEmployerAuthorizationTitle: this.epiContent?.institutionSettingsState?.epiContent?.layDBEmployerAuthorizationTitle,
			layDBEmployerAuthorizationDescription: this.epiContent?.institutionSettingsState?.epiContent?.layDBEmployerAuthorizationDescription,
			
			// QCCO Questions
			qccoQuestionTitle: this.epiContent?.institutionSettingsState?.epiContent?.qccoLayRsvpTitle,
			qccoQuestion1Label: this.epiContent?.institutionSettingsState?.epiContent?.qccoQuestion1Label,
			qccoQuestion2Label: this.epiContent?.institutionSettingsState?.epiContent?.qccoQuestion2Label,
			qccoQuestion3Label: this.epiContent?.institutionSettingsState?.epiContent?.qccoQuestion3Label,
			qccoQualificationStatusTitle: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationStatusTitle,
			qccoQualificationStatusText: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationStatusText,
			qccoQualificationYesStatusTitle: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationYesStatusTitle,
			qccoQualificationYesStatusText: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationYesStatusText,
			qccoQualificationNoStatusTitle: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationNoStatusTitle,
			qccoQualificationNoStatusText: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationNoStatusText,
			qccoQualificationErrorStatusTitle: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationErrorStatusTitle,
			qccoQualificationErrorStatusText: this.epiContent?.institutionSettingsState?.epiContent?.qccoQualificationErrorStatusText,
		};
	}

	getFormInitialValue(): DbPlanFormModel {
		return {
			adoption_date: '',
			complyEpiscopalChurchLayPensionSystem: '',
			qccoQuestion1: '',
			qccoQuestion2: '',
			qccoQuestion3: '',
		};
	}

	getFormValidationSchema() {
		const baseSchema = super.getFormValidationSchema();
		return baseSchema.shape(createQccoValidationSchema());
	}

	layDbSubmit(request: DbPlanSubmitRequest) {
		const payload = this.createSubmitModel(request?.values);
		this.currentRequest = request;
		this.dispatch(apiErrorClear());
		this.dispatch(validateDbPlan(
			request?.clientId,
			payload,
			() => this.onValidateSuccess(),
		));
	}

	getAdoptionDateOptions(): { text: string, value: string }[] {
		const currentDate = new Date();
		const options: { text: string, value: string }[] = [];

		// Add the current next 3 day of the month to the list.
		const currentDay = currentDate.getDate();
		const dateDelta = (currentDay === 1) ? 0 : 1;

		for (let i = 0; i < 3; i += 1) {
			const periodDate = new Date();
			periodDate.setMonth(currentDate.getMonth() + dateDelta + i);
			periodDate.setDate(1);
			options.push({
				text: formatDateMonthNumber(periodDate),
				value: formatDate(periodDate, false),
			});
		}

		return options;
	}

	calculateQccoStatus(values: DbPlanFormModel): QccoStatus {
		return calculateQccoStatus(values.qccoQuestion1, values.qccoQuestion2, values.qccoQuestion3);
	}

}